// Approved labels that should be used consistently throughout the UI
export const UI_LABELS = {
  USERNAME: { singular: 'Username', plural: 'Usernames' },
  HOSTNAME: { singular: 'Hostname', plural: 'Hostnames' },
  IP_ADDRESS: { singular: 'IP Address', plural: 'IP Addresses' },
  EVENT_ID: { singular: 'Event', plural: 'Events' },
  MESSAGE: { singular: 'Message', plural: 'Messages' },
  EVENT_TIMESTAMP: { singular: 'Timestamp', plural: 'Timestamps' },
} as const;

// Backend field exceptions that must use approved UI labels instead of generic transformation
export const LABEL_EXCEPTIONS: Record<string, string> = {
  host: UI_LABELS.HOSTNAME.singular,
  hostname: UI_LABELS.HOSTNAME.singular,
  source_host: UI_LABELS.HOSTNAME.singular,
  host_name: UI_LABELS.HOSTNAME.singular,
  username: UI_LABELS.USERNAME.singular,
  user_name: UI_LABELS.USERNAME.singular,
  bname: UI_LABELS.USERNAME.singular,
  ip: UI_LABELS.IP_ADDRESS.singular,
  ip_address: UI_LABELS.IP_ADDRESS.singular,
  terminal_ip: UI_LABELS.IP_ADDRESS.singular,
};

interface HumanReadableLabelOptions {
  exceptions?: Record<string, string>;
  wordMappings?: Record<string, string>;
  wordSeparators?: string[];
}

const DEFAULT_WORD_SEPARATORS = ['number', 'name', 'id', 'code', 'type', 'date', 'time'];

const DEFAULT_WORD_MAPPINGS: Record<string, string> = {
  ip: 'IP',
  sap: 'SAP',
  pers: 'Person',
  param: 'Parameter',
  addr: 'Address',
};

const APPROVED_COMPOUND_LABELS: Record<string, string> = {
  'User Name': UI_LABELS.USERNAME.singular,
  'Host Name': UI_LABELS.HOSTNAME.singular,
};

function separateKnownWords(text: string, wordSeparators: string[]): string {
  let result = text;
  const sortedWords = [...wordSeparators].sort((a, b) => b.length - a.length);

  for (const word of sortedWords) {
    const regex = new RegExp(`([a-z])(${word})(?=[A-Z]|$)`, 'gi');
    result = result.replace(regex, '$1 $2');
  }

  return result;
}

function applyWordMappings(label: string, wordMappings: Record<string, string>): string {
  const words = label.split(' ');
  const mappedWords = words.map((word) => {
    const lowerWord = word.toLowerCase();
    const mapped = wordMappings[lowerWord];
    return mapped ?? word;
  });

  return mappedWords.join(' ');
}

// Transforms any param name to a human readable label (e.g. myVar, my-var, my_var, MY_VAR => My Var)
export function toHumanReadableLabel(input: string, options: HumanReadableLabelOptions = {}): string {
  if (!input) return '';

  const {
    exceptions = LABEL_EXCEPTIONS,
    wordMappings = DEFAULT_WORD_MAPPINGS,
    wordSeparators = DEFAULT_WORD_SEPARATORS,
  } = options;

  const exceptionLabel = exceptions[input.trim().toLowerCase()];
  if (exceptionLabel) return exceptionLabel;

  let label = separateKnownWords(input, wordSeparators);
  // Replace dashes and underscores with spaces
  label = label.replaceAll('-', ' ').replaceAll('_', ' ');
  // Insert space before digits when they follow letters
  label = label.replaceAll(/([a-z])(\d)/gi, '$1 $2');
  // Insert space before capital letters (except first)
  label = label.replaceAll(/([a-z])([A-Z])/g, '$1 $2');
  // Lowercase everything, then capitalize each word
  label = label.toLowerCase().replaceAll(/\b\w/g, (char) => char.toUpperCase());
  label = applyWordMappings(label, wordMappings);
  label = APPROVED_COMPOUND_LABELS[label] ?? label;

  return label.trim();
}
